import React, { useEffect, useState } from 'react';

import { Excalidraw } from '@excalidraw/excalidraw';
import { type ImportedDataState } from '@excalidraw/excalidraw/types/data/types';
import { type ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { type AppState, type BinaryFiles, type ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';
import '../whiteboard.css';
import { Box, type BoxProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledAriaBox = styled('div')`
    border: 0;
    width: 1px;
    height: 1px;
    position: absolute;
    overflow: hidden;
    margin: -1px;
    padding: 0;
`;

const Container = styled(Box)`
    width: 100%;
    height: 100%;
`;

type ExcalidrawWrapperProps = {
    boxProps?: BoxProps<'div'>;
    initialExcalidrawData:
        | {
              key: string;
              data: ImportedDataState;
          }
        | undefined;
    excalidrawRefCallback: (value: ExcalidrawImperativeAPI | null) => void;
    onChange: (elements: readonly ExcalidrawElement[], appState: AppState, files: BinaryFiles) => void;
    gridModeEnabled: boolean;
    viewModeEnabled: boolean;
};

/**
 * Wrapper for main Excalidraw component.
 */
export const ExcalidrawWrapper = React.memo((props: ExcalidrawWrapperProps) => {
    const { i18n } = useTranslation();

    const [excalidrawRef, setExcalidrawRef] = useState();
    const [ariaLiveText, setAriaLiveText] = useState('');

    useEffect(() => {
        if (!excalidrawRef) {
            return;
        }

        const pageTextContent = props.initialExcalidrawData?.data?.elements?.find(
            (element) => !!element.customData?.pageTextContent
        )?.customData?.pageTextContent;
        if (!pageTextContent) {
            return;
        }

        const pageText = pageTextContent.items
            .map((item) => item.str.trim())
            .filter((str) => !!str)
            .join(' ');

        if (pageText) {
            setAriaLiveText(pageText);
        }
    }, [excalidrawRef, props.initialExcalidrawData?.data?.elements]);

    return (
        <Container {...props.boxProps}>
            <StyledAriaBox role="log" aria-live="polite">
                {ariaLiveText}
            </StyledAriaBox>

            <Excalidraw
                key={props.initialExcalidrawData?.key}
                initialData={props.initialExcalidrawData?.data}
                ref={(ref) => {
                    setExcalidrawRef(ref as any);
                    props.excalidrawRefCallback(ref as any);
                }}
                langCode={i18n.language}
                handleKeyboardGlobally={true}
                {...props}
            />
        </Container>
    );
});
